import { cls } from '@react/utils/classname'
import { isMobile } from '@site/js/utils/breakpoint'
import { useEffect } from 'react'

import AnchorNavigationElement from './AnchorNavigationElement'
import { getLocationHash, navigationHeight, updateLocationHash } from './AnchorNavigationHelperMethods'
import { useAnchorNavigation } from './hooks'
import { STICKY_NAVIGATION_CLASS_NAME } from './hooks/useAnchorNavigation'

type PageTitle = {
  id: string
  class: string
  label: string
  isActive: boolean
}

type AnchorNavigationProps = {
  anchornavigationarialabel: string
  anchornavigationalignment?: string
}

const header: HTMLElement = document.querySelector('.cmp-header')
const windowEventListeners = ['scroll', 'resize']

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function AnchorNavigation(props: AnchorNavigationProps) {
  const {
    temporaryPageTitles,
    setTemporaryPageTitles,
    domPageTitles,
    setDomPageTitles,
    locationHash,
    pageTitles,
    setPageTitles,
    isScrollingProgrammatically,
    isOverflowing,
    setIsOverflowing,
    setIsMobileLandscape,
    stickyNavigationContainerTopPosition,
    setStickyNavigationContainerTopPosition,
    initialAnchorNavigationPosition,
    setInitialAnchorNavigationPosition,
    isNavigationExpanded,
    setIsNavigationExpanded,
    anchorNavigationActiveItemLabel,
    setActiveLink,
    getTitlesAboveFold,
    anchorNavigationContainer,
    anchorNavigationNavbar,
    anchorNavigationList,
  } = useAnchorNavigation(props)

  useEffect(() => {
    const startTime = new Date().getTime()
    const interval = setInterval(() => {
      if (new Date().getTime() - startTime > 10000) {
        clearInterval(interval)
      }
      setTemporaryPageTitles(
        Array.from(document.querySelectorAll('h2.cmp-title__text, h2.cmp-title-v2__title')).filter(
          title => !title.hasAttribute('data-anchor-hide') && title.id && (title as HTMLElement).offsetParent !== null,
        ),
      )
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const anchorNavigationRef = anchorNavigationContainer.current
    if (!anchorNavigationRef) return

    const parentElement = anchorNavigationRef.parentElement
    const parentTop = parentElement.getBoundingClientRect().top
    const anchorTop = anchorNavigationRef.getBoundingClientRect().top

    if (window.scrollY < parentTop) {
      setInitialAnchorNavigationPosition(window.scrollY === 0 ? parentTop : window.scrollY + parentTop)
    } else {
      setInitialAnchorNavigationPosition(window.scrollY + anchorTop)
    }

    const handleScrollPage = () => {
      const isSticky = window.scrollY + header.offsetHeight >= initialAnchorNavigationPosition
      setStickyNavigationContainerTopPosition(isSticky ? `${navigationHeight(header.offsetHeight)}px` : null)
      anchorNavigationRef.classList.toggle(STICKY_NAVIGATION_CLASS_NAME, isSticky)
    }

    windowEventListeners.forEach(event => window.addEventListener(event, handleScrollPage))

    return () => {
      windowEventListeners.forEach(event => window.removeEventListener(event, handleScrollPage))
    }
  }, [anchorNavigationContainer.current, initialAnchorNavigationPosition])

  useEffect(() => {
    if (temporaryPageTitles?.length !== domPageTitles?.length) {
      setDomPageTitles(
        Array.from(document.querySelectorAll('h2.cmp-title__text, h2.cmp-title-v2__title')).filter(
          title => !title.hasAttribute('data-anchor-hide') && title.id && (title as HTMLElement).offsetParent !== null,
        ),
      )
    }
  }, [temporaryPageTitles])

  useEffect(() => {
    updateLocationHash(locationHash)
  }, [locationHash])

  useEffect(() => {
    if (domPageTitles?.length) {
      setPageTitles(
        (domPageTitles as HTMLElement[]).map(domPageTitle => {
          return {
            id: domPageTitle.id,
            class: domPageTitle.className,
            label: `${domPageTitle.dataset.anchorLabel || domPageTitle.innerText}`,
            isActive: false,
          }
        }),
      )
    }
  }, [domPageTitles])

  useEffect(() => {
    setIsOverflowing(anchorNavigationList?.current?.clientWidth < anchorNavigationList?.current?.scrollWidth)
    setIsMobileLandscape(window.innerWidth > window.innerHeight && isMobile)

    const scrollEventHandler = () => {
      // const anchorNavigationTopPosition = anchorNavigationNavbar?.current?.getBoundingClientRect().top - navigationHeight(header.offsetHeight)
      // console.log('anchorNavigationTopPosition', anchorNavigationTopPosition)
      if (!isScrollingProgrammatically) {
        const titlesAboveFold = getTitlesAboveFold()
        const activeLinkParams = titlesAboveFold?.length ? { id: titlesAboveFold.at(-1).id } : {}
        setActiveLink(activeLinkParams)
      }
    }

    window.addEventListener('scroll', scrollEventHandler)

    return () => window.removeEventListener('scroll', scrollEventHandler)
  }, [pageTitles, isScrollingProgrammatically])

  useEffect(() => {
    const initialLocalHash = getLocationHash()
    if (initialLocalHash) {
      const cleanLocationHash = initialLocalHash.slice(1)
      setActiveLink({ id: cleanLocationHash, isLoadedWithHash: true })
    } else {
      const titlesAboveFold = getTitlesAboveFold()
      const activeLinkParams = titlesAboveFold?.length ? { id: titlesAboveFold.at(-1).id } : {}
      setActiveLink(activeLinkParams)
    }
  }, [pageTitles])
  return (
    pageTitles && (
      <div
        ref={anchorNavigationContainer}
        className="cmp-anchor-navigation"
        style={stickyNavigationContainerTopPosition ? { top: `${stickyNavigationContainerTopPosition}` } : {}}
        data-dm="anchornavigation.component"
        data-cmp-is="anchorNavigation"
      >
        <nav
          ref={anchorNavigationNavbar}
          aria-label={props.anchornavigationarialabel}
          className={cls({
            'cmp-anchor-navigation__nav': true,
            'cmp-anchor-navigation__expanded': isNavigationExpanded,
          })}
        >
          <span
            className={cls({
              'cmp-anchor-navigation__toggle-span': true,
              'cmp-anchor-navigation__toggle-span--is-hidden': isNavigationExpanded,
            })}
            onClick={() => setIsNavigationExpanded(!isNavigationExpanded)}
          >
            {anchorNavigationActiveItemLabel}
          </span>
          <label htmlFor="cmp-anchor-navigation__mobile" className="cmp-anchor-navigation__toggle"></label>
          <input
            id="cmp-anchor-navigation__mobile"
            onChange={e => setIsNavigationExpanded(e.target.checked)}
            checked={isNavigationExpanded}
            type="checkbox"
          />
          <ul
            ref={anchorNavigationList}
            className={cls({
              'cmp-anchor-navigation__list': true,
              'cmp-anchor-navigation__list-centered': props.anchornavigationalignment === 'cmp-anchor-navigation__list-centered',
              'cmp-anchor-navigation__item--overflowed': isOverflowing,
            })}
          >
            {pageTitles?.map((pageTitle: PageTitle, index: number) => {
              return (
                <AnchorNavigationElement
                  key={pageTitle.id}
                  index={index}
                  id={pageTitle.id}
                  isActive={pageTitle.isActive}
                  label={pageTitle.label}
                  setActiveLink={setActiveLink}
                  expanded={isNavigationExpanded}
                />
              )
            })}
          </ul>
        </nav>
      </div>
    )
  )
}